import React, { useState } from 'react';
import { Lock } from 'lucide-react';
import { useSubscription } from '../hooks/useSubscription';
import { subscriptionService } from '../services/subscriptionService';
import UpgradeModal from './UpgradeModal';

interface ProFeatureBadgeProps {
  children: React.ReactNode;
  feature?: string;
  className?: string;
  showUpgrade?: boolean;
}

/** Subtle lock overlay for gated content – dimmed content + small lock icon, no labels or buttons. */
export function ProFeatureBadge({ 
  children, 
  feature: _feature = 'Pro feature', 
  className = '',
  showUpgrade: _showUpgrade = true 
}: ProFeatureBadgeProps) {
  const { isProUser } = useSubscription();

  if (isProUser) {
    return <>{children}</>;
  }

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <div className="opacity-60 pointer-events-none select-none">
          {children}
        </div>
        <div className="absolute top-2 right-2" aria-hidden>
          <Lock className="h-4 w-4 text-gray-400" />
        </div>
      </div>
    </div>
  );
}

// Hook to check if user has access to a feature
export function useFeatureAccess() {
  const { isProUser, profile, subscribeToPro } = useSubscription();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const checkFeatureAccess = (feature: string, showToast = true) => {
    // Pro users have unlimited access
    if (isProUser) {
      return true;
    }

    // Check specific feature limits for rookie users
    if (feature.toLowerCase().includes('practice')) {
      const hasReachedLimit = subscriptionService.hasReachedPracticeLimit(profile);
      if (hasReachedLimit && showToast) {
        setShowUpgradeModal(true);
      }
      return !hasReachedLimit;
    }

    if (feature.toLowerCase().includes('game plan')) {
      const hasReachedLimit = subscriptionService.hasReachedGamePlanLimit(profile);
      if (hasReachedLimit && showToast) {
        setShowUpgradeModal(true);
      }
      return !hasReachedLimit;
    }

    // For other features, check if user is Pro
    if (!isProUser && showToast) {
      setShowUpgradeModal(true);
    }
    return isProUser;
  };

  const handleUpgrade = () => {
    subscribeToPro();
    setShowUpgradeModal(false);
  };

  const handleCloseModal = () => {
    setShowUpgradeModal(false);
  };

  return { 
    checkFeatureAccess, 
    isProUser, 
    showUpgradeModal, 
    handleUpgrade, 
    handleCloseModal 
  };
}

// Component that provides the upgrade modal functionality
export function FeatureAccessProvider({ children }: { children: React.ReactNode }) {
  const { showUpgradeModal, handleUpgrade, handleCloseModal } = useFeatureAccess();

  return (
    <>
      {children}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={handleCloseModal}
        onUpgrade={handleUpgrade}
      />
    </>
  );
} 